import { PurchaseIcon } from '@/src/assets/icons/PurchaseIcon'
import { formatDateToShortString } from '@/src/common/utils/date'
import { Detail } from '@/src/design-system/Detail'
import { ScrollView, View, Text, StyleSheet } from 'react-native'
import { useLocalSearchParams } from 'expo-router'
import { useLoggedInQuery } from '@/src/common/gql/MesaGql'
import {
  type GetTransactionEventDetailsQuery,
  type GetTransactionEventDetailsQueryVariables,
  CardTransactionProcessingType,
} from '@api/mesa/graphql'
import { Loading } from '@/src/common/components/Loading'
import { graphql } from '@api/mesa'
import { Fragment } from 'react'
import { useLogError } from '@/src/common/hooks/useLogError'
import { Button } from '@/src/common/components/Button'
import { openInAppBrowser } from '@/src/common/utils/openInAppBrowser'
import { useCardDisputeURL } from '@/src/services/Statsig/useCardDisputeURLParameterStore'
import { startCase } from 'lodash'
import { formatCurrency } from '@/src/common/utils/general'
import { isProduction } from '@/src/common/environment'
import { DesignSystem } from '@/src/design-system'
import { mesaColorNames, typography } from '@/src/common/theme'
import * as Components from '../../components'

function TransactionEventDetailsLoaded({
  transactionEventDetails: {
    referenceNumber,
    merchantDetails,
    renderedAmount,
    createdAt,
    pointsEarns,
    transactionProcessingType,
  },
}: GetTransactionEventDetailsQuery) {
  const cardDisputeURL = useCardDisputeURL()
  const disputeURL = isProduction
    ? cardDisputeURL.get('dispute_url', '')
    : 'https://www.mesamember.com' // Do not take customer to dispute page in staging
  const merchantName = merchantDetails?.name
  const postedDate = formatDateToShortString(new Date(createdAt))
  const totalPointsEarned =
    pointsEarns?.reduce((total, pointsEarn) => total + pointsEarn.points, 0) ?? 0
  const formattedPointsEarned = Number(totalPointsEarned).toLocaleString()

  return (
    <ScrollView contentContainerStyle={{ paddingBottom: 50, gap: 16 }}>
      <DesignSystem.Card style={styles.styledCardAdditionalStyles}>
        <View style={styles.cardContainer}>
          <DesignSystem.BoxedIcon Icon={PurchaseIcon} />
          <Text
            style={[
              { color: mesaColorNames['global/grey/70 #53575A'] },
              typography['paragraph/md'],
              !merchantName && { fontStyle: 'italic' },
            ]}
          >
            {merchantName ?? 'N/A'}
          </Text>
          <Text style={typography['heading/heading3']}>
            {`${formatCurrency(renderedAmount.amount, {
              isFloat: true,
              minimumFractionDigits: 2,
              decimals: null,
            })} ${renderedAmount.currencyCode}`}
          </Text>
          <Text style={styles.dateText}>{postedDate}</Text>
        </View>
      </DesignSystem.Card>

      {pointsEarns?.length ? (
        <DesignSystem.Card style={styles.styledCardAdditionalStyles}>
          <View style={styles.cardContainer}>
            <Text style={typography['label/xs-bold']}>Reward details</Text>
            <Text style={[typography['heading/heading3'], styles.pointsText]}>
              {formattedPointsEarned}
            </Text>
            <Text style={styles.mesaPoints}>Mesa Points</Text>
            {pointsEarns.map((pointsEarn) => (
              <Fragment key={pointsEarn.reason}>
                <View
                  style={{
                    backgroundColor: mesaColorNames['Greyscale/400 #A4AAAC'],
                    height: 1,
                    width: 16,
                  }}
                />
                <Text style={styles.pointsEarnReason}>{pointsEarn.reason}</Text>
              </Fragment>
            ))}
          </View>
        </DesignSystem.Card>
      ) : null}

      <DesignSystem.Card style={styles.styledCardAdditionalStyles}>
        <View style={styles.cardContainer}>
          <Text style={typography['label/xs-bold']}>Transaction details</Text>
          <Detail
            category="Type"
            description={
              transactionProcessingType === CardTransactionProcessingType.Returns
                ? 'Return'
                : 'Sale'
            }
          />
          <Detail category="Posted Date" description={postedDate} />
          <Detail category="Reference Number" description={referenceNumber} />
          <Detail
            category="Merchant Category"
            description={
              merchantDetails?.category ? startCase(merchantDetails.category) : undefined
            }
          />
          <Detail
            category="Merchant Details"
            description={[
              merchantName,
              merchantDetails?.address?.streetAddress,
              merchantDetails?.address?.extendedAddress,
              merchantDetails?.address?.locality,
              merchantDetails?.address?.region,
              merchantDetails?.address?.postalCode,
              merchantDetails?.address?.countryCodeAlpha3,
            ]
              .filter((value) => value)
              .join(', ')}
          />
          {merchantDetails?.description && (
            <Detail category="Merchant Description" description={merchantDetails.description} />
          )}
        </View>
      </DesignSystem.Card>
      <Button
        title="Need help with this transaction?"
        onPress={() => void openInAppBrowser(disputeURL, 'Help')}
      />
    </ScrollView>
  )
}

export function TransactionEventDetails() {
  const { id = '' } = useLocalSearchParams<GetTransactionEventDetailsQueryVariables>()
  const onError = useLogError()

  const { data, loading } = useLoggedInQuery(
    graphql(`
      query GetTransactionEventDetails($id: String!) {
        transactionEventDetails(transactionEventId: $id) {
          referenceNumber
          merchantDetails {
            name
            address {
              streetAddress
              extendedAddress
              locality
              region
              postalCode
              countryCodeAlpha3
            }
            category
            description
          }
          createdAt
          renderedAmount {
            amount
            currencyCode
          }
          pointsEarns {
            points
            reason
          }
          transactionProcessingType
          type
        }
      }
    `),
    { fetchPolicy: 'no-cache', variables: { id }, skip: !id, onError },
  )

  return loading ? (
    <ScrollView contentContainerStyle={{ paddingBottom: 50 }}>
      <Loading />
    </ScrollView>
  ) : data ? (
    <TransactionEventDetailsLoaded {...data} />
  ) : (
    <>
      <Components.Highnote.NoTransactionsIcon
        svgProps={{ style: { marginHorizontal: 'auto', marginTop: 32 } }}
        pathProps={{ fill: mesaColorNames['Mesa - Brand/White #FFFFFF'] }}
      />
      <Text
        style={[
          typography.L,
          {
            textAlign: 'center',
            color: mesaColorNames['Mesa - Brand/White #FFFFFF'],
            marginTop: 32,
            marginBottom: 32,
          },
        ]}
      >
        Transaction not found
      </Text>
    </>
  )
}

const styles = StyleSheet.create({
  cardContainer: { display: 'flex', gap: 8 },
  styledCardAdditionalStyles: {
    paddingLeft: 16,
    paddingRight: 16,
    paddingTop: 16,
    paddingBottom: 16,
    marginHorizontal: 16,
    display: 'flex',
    gap: 24,
  },
  dateText: { color: mesaColorNames['Greyscale/500 #6E7375'], fontSize: 12 },
  pointsText: { color: mesaColorNames['Mesa Primary Scale/400 - Brand #00DFC9'] },
  mesaPoints: {
    ...typography['label/md-regular'],
    color: mesaColorNames['global/grey/80 #2F3133'],
  },
  pointsEarnReason: {
    ...typography['label/sm-regular'],
    color: mesaColorNames['Greyscale/500 #6E7375'],
  },
})
